import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { toast, Toaster } from 'react-hot-toast';
import Button from '../components/ui/Button';
import SectionHeading from '../components/ui/SectionHeading';
import { publishedArticles as articles, formatArticleDate } from '../data/articles';

const BlogList: React.FC = () => {
  const [formData, setFormData] = useState({
    email: '',
    phone: '',
    topic: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const formDataObj = new FormData();
      formDataObj.append('form-name', 'blog-topic-suggestion');
      formDataObj.append('email', formData.email);
      formDataObj.append('phone', formData.phone);
      formDataObj.append('topic', formData.topic);

      const response = await fetch('/', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(formDataObj as any).toString(),
      });

      if (response.ok) {
        toast.success('Topic suggestion submitted successfully!');
        setFormData({ email: '', phone: '', topic: '' });
      } else {
        throw new Error('Form submission failed');
      }
    } catch (error) {
      toast.error('Failed to submit topic suggestion. Please try again.');
      console.error('Error:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="py-20 bg-[#FAFAF5]">
      <Toaster position="top-center" />
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <SectionHeading
          title="Blog Articles"
          subtitle="Explore our collection of articles on property management insights, trends, and best practices. Email us at info@rentsparks.com if you'd like our vetted LA metro area vendor list or our property management checklist as a downloadable PDF."
        />

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mb-16">
          {articles.map((article) => (
            <Link
              key={article.slug}
              to={`/blog/${article.slug}`}
              className="bg-white rounded-lg overflow-hidden shadow-custom hover:shadow-custom-hover transition-all duration-300"
            >
              <div className="p-8">
                <h3 className="font-abadi text-xl font-medium mb-4 leading-tight">{article.title}</h3>
                <p className="text-dark-gray mb-6 text-lg leading-relaxed">{article.excerpt}</p>
                <div className="flex flex-wrap gap-2 mb-4">
                  {article.tags.map(tag => (
                    <span
                      key={tag}
                      className="text-sm bg-light-gray px-3 py-1 rounded-full text-dark-gray"
                    >
                      {tag}
                    </span>
                  ))}
                </div>
                <div className="text-base text-dark-gray">{formatArticleDate(article.publishDate)}</div>
              </div>
            </Link>
          ))}
        </div>

        <div className="max-w-2xl mx-auto bg-white rounded-lg shadow-custom p-8">
          <h3 className="font-abadi text-2xl font-medium mb-6">Suggest a Blog Topic</h3>
          <form
            onSubmit={handleSubmit}
            name="blog-topic-suggestion"
            method="POST"
            data-netlify="true"
            netlify-honeypot="bot-field"
            className="space-y-6"
          >
            <input type="hidden" name="form-name" value="blog-topic-suggestion" />
            <p className="hidden">
              <label>Don't fill this out: <input name="bot-field" /></label>
            </p>

            <div>
              <label className="block text-dark-gray mb-2 text-lg">
                Email <span className="text-error">*</span>
              </label>
              <input
                type="email"
                name="email"
                required
                value={formData.email}
                onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
                className="w-full px-4 py-3 rounded-md border border-medium-gray focus:outline-none focus:border-secondary text-lg"
                placeholder="your@email.com"
              />
            </div>
            <div>
              <label className="block text-dark-gray mb-2 text-lg">
                Phone Number
              </label>
              <input
                type="tel"
                name="phone"
                value={formData.phone}
                onChange={(e) => setFormData(prev => ({ ...prev, phone: e.target.value }))}
                className="w-full px-4 py-3 rounded-md border border-medium-gray focus:outline-none focus:border-secondary text-lg"
                placeholder="(424) 277-7216"
              />
            </div>
            <div>
              <label className="block text-dark-gray mb-2 text-lg">
                Suggested Topic <span className="text-error">*</span>
              </label>
              <textarea
                name="topic"
                required
                value={formData.topic}
                onChange={(e) => setFormData(prev => ({ ...prev, topic: e.target.value }))}
                className="w-full px-4 py-3 rounded-md border border-medium-gray focus:outline-none focus:border-secondary text-lg"
                rows={4}
                placeholder="What topic would you like us to cover?"
              ></textarea>
            </div>
            <Button
              variant="secondary"
              type="submit"
              className="w-full text-lg py-3"
              disabled={isSubmitting}
            >
              {isSubmitting ? 'Submitting...' : 'Submit Topic Suggestion'}
            </Button>
          </form>
        </div>
      </div>
    </div>
  );
};

export default BlogList;
